import { CustomTextarea } from "./CustomFormElements"


const handleDragOver = (event) => {
    event.preventDefault(); // Allow the drop
    const textarea = event.target;

    // Define offsets to adjust cursor position
    const VERTICAL_OFFSET = 60;
    const HORIZONTAL_OFFSET = 20;

    // Get the bounding rectangle of the textarea
    const { left, top, width, height } = textarea.getBoundingClientRect();
    const mouseX = event.clientX - left - HORIZONTAL_OFFSET; // Horizontal mouse position with offset
    const mouseY = event.clientY - top - VERTICAL_OFFSET; // Vertical mouse position with offset

    // Approximate font metrics for the textarea
    const fontWidth = 8; // Width of a character in pixels
    const lineHeight = 19; // Height of a single line in pixels
    const charsPerLine = Math.floor(width / fontWidth);

    // Split the textarea content into lines, accounting for wrapping
    const lines = [];
    textarea.value.split('\n').forEach((line) => {
        for (let i = 0; i < line.length; i += charsPerLine) {
            lines.push(line.slice(i, i + charsPerLine));
        }
        lines.push(''); // Account for newline characters
    });

    // Calculate the line index based on vertical mouse position
    const lineIndex = Math.min(
        Math.max(0, Math.floor(mouseY / lineHeight)), // Ensure the line index is within bounds
        lines.length - 1
    );

    // Calculate the character index within the current line
    const charInLine = Math.min(
        Math.max(0, Math.floor(mouseX / fontWidth)), // Ensure the char index is within bounds
        lines[lineIndex]?.length || 0
    );

    // Calculate the exact cursor position in the textarea's content
    let position = 0;
    for (let i = 0; i < lineIndex; i++) {
        position += (lines[i]?.length || 0) + 1; // Include newline characters
    }
    position += charInLine;

    // Ensure the position is within bounds
    position = Math.min(Math.max(0, position), textarea.value.length);

    // Set the cursor position dynamically
    textarea.focus();
    textarea.setSelectionRange(position, position);
};



const handleDrop = (event) => {
    event.preventDefault();
    const droppedEntity = event.dataTransfer.getData('text/plain'); // Get the dragged entity
    const textarea = event.target;

    // Add spaces before and after the entity
    const formattedEntity = ` ${droppedEntity} `;

    // Insert the text at the current cursor position
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;

    textarea.value =
        currentValue.substring(0, start) + formattedEntity + currentValue.substring(end);

    // Move the cursor to the end of the inserted text
    const newCursorPosition = start + formattedEntity.length;
    textarea.setSelectionRange(newCursorPosition, newCursorPosition);

    textarea.focus(); // Keep focus on the textarea
};


export const SimplePrompt = ({
    isDisabled,
    promptValue,
    setPromptValue
}) => {

    return (
        <div className="simple-text-pattern w-100">
            
            <h3 className="h3-title">Simple Prompt</h3>
            
            <div className="simple-text mb-4 mt-5">
                <CustomTextarea 
                    id="simple-text" // Add a unique id
                    label="Prompt"
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={promptValue}
                    onChange={(value) => setPromptValue(value)} // Update the state on change
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler
                />
            </div>
            
        </div>
    );
};

export const RealtimePrompt = ({
    isDisabled,
    speakerValue,
    setSpeakerValue,
    listenerValue,
    setListenerValue
}) => {
    return (
        <div>
            <div className="simple-text-pattern w-100">

                <h3 className="h3-title">Realtime</h3>

                <div className="simple-text mb-4 mt-5">
                    <CustomTextarea
                        id="speaker"
                        label="Speaker"
                        mandatory={true}
                        disabled={isDisabled}
                        addedText={speakerValue}
                        onChange={(value) => setSpeakerValue(value)}
                        resizable={false}
                        height="300px"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    />
                </div>

                <div className="simple-text mb-4 mt-5">
                    <CustomTextarea
                        id="listener"
                        label="Listener"
                        mandatory={true}
                        disabled={isDisabled}
                        addedText={listenerValue}
                        onChange={(value) => setListenerValue(value)}
                        resizable={false}
                        height="300px"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    />
                </div>
            </div>
        </div>
    )

};


export const RodesPattern = ({
    roleValue,
    setRoleValue,
    objectiveValue,
    setObjectiveValue,
    detailsValue,
    setDetailsValue,
    examplesValue,
    setExamplesValue,
    checkValue,
    setCheckValue,
    isDisabled
  
}) => {

    return (
        <div className="rodes-pattern w-100">
            
            <h3 className="h3-title">RODES</h3>
            <div className="pettern-description max-width-700 regular small-font">
                <ul className="list-no-bullets">
                    <li>R - Role: Insert desired role you want AI to take.</li>
                    <li>O - Objective: Insert objective you want AI to do.</li>
                    <li>D - Details: Insert any context or constraints AI needs to create a good output</li>
                    <li>E - Examples: Here are good examples you can use to model your answer. Insert examples of good outputs</li>
                    <li>S - Sense Check: Do you understand the objective and the specific guidelines for this task?</li>
                </ul>
                <p>RODES is particularly suited for projects requiring a high degree of 
                    specificity and clarity, such as educational content, customer 
                    service protocols, and detailed research inquiries. 
                    Its structured approach ensures that AI-generated content meets 
                    precise standards and objectives.
                </p>
            </div>
            
            <div className="role mb-4 mt-5">
                <CustomTextarea 
                    id="rodes-role" // Add a unique id
                    label="Role"
                    sublabel='Example: "You are a seasoned copywriter specialized in crafting viral tweets."'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={roleValue}
                    onChange={(value) => setRoleValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="objective mb-4">
                <CustomTextarea 
                    id="rodes-objective" // Add a unique id
                    label="Objective"
                    sublabel='Example: "Example: Write a twitter hook that aims to go viral on the topic of {industry_name} 
                    Show the example of the tween with the share button."'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={objectiveValue}
                    onChange={(value) => setObjectiveValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="details mb-4">
                <CustomTextarea 
                    id="rodes-details" // Add a unique id
                    label="Details"
                    sublabel='Example: 
                        The tweet should be no longer than 280 characters.
                        Use powerful and persuasive language.
                        Do not include hashtags or emojis.'      
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={detailsValue}
                    onChange={(value) => setDetailsValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="examples mb-4">
                <CustomTextarea 
                    id="rodes-examples" // Add a unique id
                    label="Examples"
                    sublabel='Example: 
Here are some examples you should use to model your answer
<ol>
<li>"Credit card debt is killing your financial progress.
Here is how you get rid of it ASAP:"</li>
</ol>
'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={examplesValue}
                    onChange={(value) => setExamplesValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="check mb-4">
                <CustomTextarea 
                    id="rodes-check" // Add a unique id
                    sublabel='Example: Do you understand the objective and the specific guidelines for this task?'
                    label="Check"
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={checkValue}
                    onChange={(value) => setCheckValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
        </div>
    );
};



export const RisenPattern = ({
    roleValue,
    setRoleValue,
    instructionValue,
    setInstructionValue,
    stepsValue,
    setStepsValue,
    goalValue,
    setGoalValue,
    narrowingValue,
    setNarrowingValue,
    isDisabled
}) => {

    return (
        <div className="risen-pattern w-100">
            
            <h3 className="h3-title">RISEN</h3>
            <div className="pettern-description max-width-700 regular small-font">
                <ul className="list-no-bullets">
                    <li>R - Role: Insert desired role you want AI to take.</li>
                    <li>I - Instruction: Insert the task you want AI to complete.</li>
                    <li>S - Steps to complete: Insert numbered list of steps to follow.</li>
                    <li>E - End goal: Insert goal of the output.</li>
                    <li>N - Narrowing: Enter constraints.</li>
                </ul>
                <p>The RISEN framework provides a structured approach to dissect complex 
                    or constrained tasks, such as blog posts or research projects, into actionable 
                    components for better execution and focus.
                </p>
            </div>
            
            <div className="role mb-4 mt-5">
                <CustomTextarea 
                    id="risen-role" // Add a unique id
                    label="Role"
                    sublabel='Example: "You are an expert digital course builder who has sold millions in online courses."'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={roleValue}
                    onChange={(value) => setRoleValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="risen-instruction mb-4">
                <CustomTextarea 
                    id="risen-instruction" // Add a unique id
                    label="Instruction"
                    sublabel='Example: "Please give me a list of EVERYTHING important that I should include 
                    in my AI course and tell me all the different methods of growth I can implement to maximise revenue."'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={instructionValue}
                    onChange={(value) => setInstructionValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="steps mb-4">
                <CustomTextarea 
                    id="risen-steps" // Add a unique id
                    label="Steps to complete"
                    sublabel='Example: 
                    <ol>    
                        <li>First start by covering all the things that ANY digital course should include.</li>
                        <li>Then proceed by giving your thoughts on what AI courses should include.</li>
                        <li>End with covering the best growth marketing tactics and strategies for digital courses.</li>
                    </ol>'      
                    
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={stepsValue}
                    onChange={(value) => setStepsValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="goal mb-4">
                <CustomTextarea 
                    id="risen-goal" // Add a unique id
                    label="Goal"
                    sublabel='Example: 
                     "The goal is to give me a concise list of everything I should include within 
                     the course, as well as give me ideas on how I can maximize the revenue from my course."'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={goalValue}
                    onChange={(value) => setGoalValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="risen-narrowing mb-4">
                <CustomTextarea 
                    id="risen-narrowing" // Add a unique id
                    sublabel='Example:  "Maximum of 500 words. - Avoid technical jargon. - Make it actionable - Make it clear."'
                    label="Narrowing"
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={narrowingValue}
                    onChange={(value) => setNarrowingValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
        </div>
    );
};

export const SwarmPattern = ({
    roleValue,
    setRoleValue,
    instructionValue,
    setInstructionValue,
    isDisabled
}) => {
    return (
        <>
            <div className="swarm-pattern w-100">
                <h3 className="h3-title">Swarm</h3>
                <div className="pettern-description max-width-700 regular small-font">
                    <ul className="list-no-bullets">
                        <li>R - Role: Insert desired role you want AI to take.</li>
                        <li>I - Instruction: Insert the task you want AI to complete.</li>
                    </ul>
                    <p>Swarm is designed to harness the collective intelligence of multiple AI agents,
                        each contributing unique insights or perspectives to tackle complex problems or
                        generate innovative ideas.
                    </p>
                </div>
                <div className="role mb-4 mt-5">
                    <CustomTextarea 
                        id="risen-role" // Add a unique id
                        label="Role"
                        sublabel='Example: "You are an expert digital course builder who has sold millions in online courses."'
                        mandatory={true}
                        disabled={isDisabled}
                        addedText={roleValue}
                        onChange={(value) => setRoleValue(value)}
                        resizable={false}
                        height="300px"
                        onDragOver={handleDragOver} // Attach drag-over handler
                        onDrop={handleDrop} // Attach drop handler  
                    />
                </div>
                
                <div className="risen-instruction mb-4">
                    <CustomTextarea 
                        id="risen-instruction" // Add a unique id
                        label="Instruction"
                        sublabel='Example: "Please give me a list of EVERYTHING important that I should include 
                        in my AI course and tell me all the different methods of growth I can implement to maximise revenue."'
                        mandatory={true}
                        disabled={isDisabled}
                        addedText={instructionValue}
                        onChange={(value) => setInstructionValue(value)}
                        resizable={false}
                        height="300px"
                        onDragOver={handleDragOver} // Attach drag-over handler
                        onDrop={handleDrop} // Attach drop handler  
                    />
                </div>
                
            </div>
        </>
    )
}

export const CoDPattern = ({
    instructionValue,
    setInstructionValue,
    recursionValue,
    setRecursionValue,
    benchmarkValue,
    setBenchmarkValue,
    guidelinesValue,
    setGuidelinesValue,
    isDisabled
}) => {

    return (
        <div className="cod-pattern w-100">
            
            <h3 className="h3-title">Chain of Density</h3>
            <div className="pettern-description max-width-700 regular small-font">
                <ul className="list-no-bullets">
                    <li>Instruction</li>
                    <li>Recursion</li>
                    <li>Benchmark</li>
                    <li>Additional guidelines</li>
                </ul>
                <p>It’s amazing for article summaries, improving longer-form content 
                    via recursion or even improving existing prompts.
                </p>
                <strong>How the pattern works:</strong>
                <ul>
                    <li>Introduce the topic with a brief synopsis or statement.</li>
                    <li>Choose the key details from the initial summary that are most important.</li>
                    <li>Shorten the summary by rewriting it with these important parts included.</li>
                    <li>Examine the updated summary to make sure no important details are missing.</li>
                    <li>While aiming for concision, incorporate this information into the summary.</li>
                    <li>Continue steps 3-5 until the result’s density and conciseness meet your requirements or for a predetermined number of iterations.</li>
                </ul>
                

            </div>
            
            <div className="role mb-4 mt-5">
                <CustomTextarea 
                    id="cod-instruction" // Add a unique id
                    label="Instruction"
                    sublabel='Example: "Here is a prompt I use to generate cold emails 
                    for my landing page creation agency: "I need a cold email idea that 
                    will engage my [ideal customer] with a unique perspective on 
                    [subject] and persuade them to take [desired action]."'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={instructionValue}
                    onChange={(value) => setInstructionValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="cod-recursion mb-4">
                <CustomTextarea 
                    id="cod-recursion" // Add a unique id
                    label="Recursion"
                    sublabel='Example: "Repeat the following 2 steps 5 times.
<ul>
    <li>Step 1. Identify 1-3 points from the initial output which are missing.</li>
    <li>Step 2. Write a new, improved output of identical length which includes the missing points.</li>
</ul>'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={recursionValue}
                    onChange={(value) => setRecursionValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
            
            <div className="cod-benchmark mb-4">
                <CustomTextarea 
                    id="cod-benchmark" // Add a unique id
                    label="Benchmark"
                    sublabel='Example: Here is more information on what makes a good prompt:
Clarity and Specificity
<ul>
    <li><strong>Be Clear:</strong> The prompt should be easy to understand.</li>
    <li><strong>Be Specific:</strong> Vague questions will often yield vague answers. The more specific you are, the better the AI can generate a targeted response.</li>
    <li><strong>Open-Ended vs Closed-Ended</strong></li>
    <ul>
        <li><strong>Open-Ended:</strong> Use open-ended questions for comprehensive answers or when you want the AI to generate multiple ideas.</li>
        <li><strong>Closed-Ended:</strong> Use yes/no or either/or questions when you need a straightforward answer.</li>
    </ul>
    <li><strong>Provide Context:</strong> The more context you give, the more the AI understands the scenario you are asking about.</li>
    <li><strong>Time-Frame:</strong> If the question is dependent on a particular time or sequence, make sure to include it.</li>
    <li><strong>Define Your Objective:</strong> Whether it is to gather information, generate text, solve a problem, or something else, make your objective clear.</li>
    <li><strong>Question Type:</strong> Tailor your prompt based on what you are looking for—explanations, summaries, creative writing, etc.</li>
    <li><strong>Avoid Jargon:</strong> Unless it is necessary, steer clear of complex language or industry-specific terms.</li>
    <li><strong>K.I.S.S. (Keep It Simple, Stupid):</strong> Sometimes, simpler prompts are more effective in getting the desired response.</li>
</ul>'      
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={benchmarkValue}
                    onChange={(value) => setBenchmarkValue(value)}
                    resizable={false}
                    height="300px"
                />
            </div>
            
            <div className="guidelines mb-4">
                <CustomTextarea 
                    id="cod-guidelines" // Add a unique id
                    label="Additional guidelines"
                    sublabel='Example: 
                     "<strong>Guidelines:</strong>
<ul>
    <li>The first summary should be long (4-5 sentences, ~80 words) yet highly non-specific, containing little information beyond the entities marked as missing. Use overly verbose language and fillers (e.g., "this article discusses") to reach ~80 words.</li>
    <li>Make every word count: rewrite the previous summary to improve flow and make space for additional entities.</li>
    <li>Make space with fusion, compression, and removal of uninformative phrases like "the article discusses".</li>
    <li>The summaries should become highly dense and concise yet self-contained, i.e., easily understood without the article.</li>
    <li>Missing entities can appear anywhere in the new summary.</li>
    <li>Never drop entities from the previous summary. If space cannot be made, add fewer new entities.</li>
</ul>
'
                    mandatory={true}
                    disabled={isDisabled}
                    addedText={guidelinesValue}
                    onChange={(value) => setGuidelinesValue(value)}
                    resizable={false}
                    height="300px"
                    onDragOver={handleDragOver} // Attach drag-over handler
                    onDrop={handleDrop} // Attach drop handler  
                />
            </div>
        </div>
    );
};

